<template>
  <v-row>
    <v-col>
      <v-card>
        <v-card-title class="py-0">
          <h2 class="text-h2 mt-10 mb-7">
            {{ $t("communicationPanelStrings.title") }}
          </h2>
        </v-card-title>
        <v-card-text>
          <v-row v-if="canAddComment">
            <v-col>
              <v-alert
                type="info"
                border="start"
                border-color
                density="compact"
                prominent
                closable
                elevation="1"
              >
                {{ $t("communicationPanelStrings.visibilityInfoText") }}<br>
                <br>
                {{ $t("communicationPanelStrings.senderEmailInfoText") }}<br>
                {{ $t("communicationPanelStrings.recipientEmailInfoText")
                }}<br>
                {{
                  $t("communicationPanelStrings.recipientPersonEmailInfoText")
                }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              v-if="canAddComment"
              cols="12"
              md="6"
              order-md="2"
            >
              <v-card color="background-active">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h3 class="text-h3">
                        {{ $t("communicationPanelStrings.newCommentSubtitle") }}
                      </h3>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-select
                        v-model="reply.recipients"
                        multiple
                        item-value="id"
                        item-title="name"
                        chips
                        closable-chips
                        :items="displayOrgs"
                        :label="
                          $t(
                            'communicationPanelStrings.placeholders.recipients'
                          )
                        "
                        name="payingOrgs"
                        density="compact"
                        variant="outlined"
                        bg-color="white"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <vee-form ref="observer">
                        <vee-field
                          v-slot="{ componentField, errors }"
                          v-model="reply.content"
                          rules="min:3|max:500"
                          :name="
                            $t(
                              'communicationPanelStrings.placeholders.replyContent'
                            )
                          "
                        >
                          <v-textarea
                            :label="
                              $t(
                                'communicationPanelStrings.placeholders.replyContent'
                              )
                            "
                            v-bind="componentField"
                            clearable
                            :error-messages="errors"
                            variant="outlined"
                            density="compact"
                            counter="500"
                            rows="4"
                            auto-grow
                            bg-color="white"
                            @update:model-value="$emit('comment-changed', reply.content)"
                          />
                        </vee-field>
                      </vee-form>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-btn
                        rounded
                        class="primary-button"
                        :block="xs"
                        :disabled="!reply.content || sendingReply"
                        :loading="sendingReply"
                        @click="sendReply"
                      >
                        {{
                          $t("communicationPanelStrings.labels.sendReplyBtn")
                        }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              v-if="showCannotAddCommentWarning"
              cols="12"
              md="6"
              order-md="2"
            >
              <v-alert type="warning">
                {{ $t("communicationPanelStrings.disabledText") }}
              </v-alert>
            </v-col>
            <v-col
              v-if="isArchived"
              cols="12"
              md="6"
              order-md="2"
            >
              <v-alert type="warning">
                {{ $t("communicationPanelStrings.disabledByArchivedText") }}
              </v-alert>
            </v-col>
            <v-col
              cols="12"
              md="6"
              order-md="1"
            >
              <v-card>
                <v-card-text>
                  <v-row class="background-active temp-margin-fix">
                    <v-col>
                      <v-row>
                        <v-col>
                          <h3 class="text-h5">
                            {{ $t("communicationPanelStrings.filterSubtitle") }}
                          </h3>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12">
                          <button-filter
                            v-model="communicationTypeFilter"
                            v-model:clearing="clearingCommunicationTypeFilter"
                            :initial="communicationTypeFilter"
                            :options="communicationTypeOptions"
                            mandatory
                            css-class="flex-column flex-md-row"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-select
                            v-model="orgsRelatedComments"
                            :label="
                              $t(
                                'communicationPanelStrings.labels.filterCommentsForOrg'
                              )
                            "
                            bg-color="white"
                            multiple
                            item-value="id"
                            item-title="name"
                            chips
                            closable-chips
                            :items="displayOrgs"
                            density="compact"
                            variant="outlined"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col v-if="hasFilteredEvents">
                      <v-timeline density="compact">
                        <div
                          v-for="(event, index) in filteredEvents"
                          :key="index"
                        >
                          <comment-bubble
                            v-if="isComment(event)"
                            :comment="event"
                            :own-comment="isOwnComment(event)"
                          />
                          <log-event
                            v-else
                            :event="event"
                          />
                        </div>
                      </v-timeline>
                    </v-col>
                    <v-col v-if="!hasFilteredEvents">
                      <em>{{
                        $t("communicationPanelStrings.emptyCommentSection")
                      }}</em>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import LogEvent from "./components/LogEvent";
import CommentBubble from "./components/CommentBubble";
import { getOrgDisplayName } from "@/utils/org";
import { api, urls } from "@/utils/api";
import { Form as VeeForm, Field as VeeField } from "vee-validate";
import validationErrors from "@/mixins/validationErrors";
import ButtonFilter from "@/views/common/components/ButtonFilter";
import CommunicationTypes from "@/enums/CommunicationTypes";
import { mapGetters } from "vuex";
import { handleError } from "@/utils/errorHandler";
import ApplicationDatabaseSource from "@/enums/ApplicationDatabaseSource";
import { useDisplay } from 'vuetify';

export default {
  name: "CommunicationPanel",
  components: {
    LogEvent,
    CommentBubble,
    ButtonFilter,
    VeeForm,
    VeeField
  },
  mixins: [validationErrors],
  props: {
    log: Array,
    orgs: Array,
    comments: Array,
    applicationId: String,
    applicationPersonId: Number,
    currentUser: Object,
    currentOrgId: Number,
    canWriteAsOrg: Boolean,
    isDraft: Boolean,
    initializing: Boolean,
    source: Number
  },
  setup() {
    const { xs } = useDisplay();
    return { xs };
  },
  data: function () {
    return {
      reply: {
        content: "",
        orgId: null,
        personId: null,
        recipients: []
      },
      communicationTypeFilter: this.getCommunicationTypeOptions().map(
        x => x.value
      ),
      communicationTypeOptions: this.getCommunicationTypeOptions(),
      orgsRelatedComments: [],
      clearingCommunicationTypeFilter: false
    };
  },
  computed: {
    ...mapGetters({
      urlState: "utilsRestStore/urlState"
    }),
    filteredEvents() {
      let events = [];

      if (this.log && this.showLogEvents) {
        for (const logEvent of this.log) {
          events.push(logEvent);
        }
      }

      if (this.comments && this.showComments) {
        for (const comment of this.comments) {
          if (
            !this.orgsRelatedComments.length ||
            comment.recipients.some(x => this.orgsRelatedComments.includes(x))
          ) {
            events.push(comment);
          }
        }
      }

      events = events.sort((x, y) => new Date(x.date) - new Date(y.date));

      return events;
    },
    hasFilteredEvents() {
      return this.filteredEvents?.length;
    },
    showLogEvents() {
      return this.communicationTypeFilter.includes(CommunicationTypes.Log);
    },
    showComments() {
      return this.communicationTypeFilter.includes(CommunicationTypes.Comment);
    },
    showCannotAddCommentWarning() {
      return !this.initializing && !this.canAddComment && !this.isArchived;
    },
    commentAsReceiver() {
      return this.applicationPersonId !== this.currentPersonId;
    },
    currentPersonId() {
      if (!this.currentUser) {
        return null;
      }

      return parseInt(this.currentUser.personid, 10);
    },
    displayOrgs() {
      return this.orgs.map(x => ({ id: x.id, name: getOrgDisplayName(x) }));
    },
    sendingReply() {
      if (this.applicationId) {
        return this.urlState(urls.v1.applications.reply(this.applicationId));
      }
      return false;
    },
    canAddComment() {
      return (
        !this.isArchived &&
        !this.isDraft &&
        (this.canWriteAsOrg ||
          this.applicationPersonId === this.currentPersonId)
      );
    },
    isArchived() {
      return this.source === ApplicationDatabaseSource.Archived;
    }
  },
  mounted() {
    this.clearingCommunicationTypeFilter = true;
  },
  methods: {
    isComment(event) {
      return event.logEventType === undefined || event.logEventType === null;
    },
    isOwnComment(comment) {
      if (!this.isComment(comment)) {
        return false;
      }

      if (comment.sender.orgId) {
        return comment.sender.orgId === this.currentOrgId;
      }

      return comment.sender.personId === this.currentPersonId;
    },
    async sendReply() {
      // validation
      const isDataValid = await this.$refs.observer.validate();

      if (!isDataValid) {
        this.showValidationErrorSnackbar(this.$refs.observer);
        return;
      }

      // init reply sender
      if (this.commentAsReceiver) {
        this.reply.orgId = this.currentOrgId;
      } else {
        this.reply.orgId = null;
      }

      this.reply.personId = this.currentPersonId;

      // send reply
      try {
        const response = await api.v1.applications.reply(
          this.applicationId,
          this.reply
        );

        if (response.status === 201) {
          this.reply.content = "";
          this.reply.recipients = [];

          this.$emit("reply", response.data);
          this.$emit("comment-changed", this.reply.content);
        }
      } catch (error) {
        await handleError(error);
      }
    },
    getCommunicationTypeOptions() {
      return [
        {
          value: CommunicationTypes.Log,
          label: this.$t("communicationPanelStrings.labels.showLogEvents")
        },
        {
          value: CommunicationTypes.Comment,
          label: this.$t("communicationPanelStrings.labels.showComments")
        }
      ];
    }
  }
};
</script>

<style scoped>
/* Should probably be fixed through markup or other css fixes, but this at least properly aligns the active background with the borders for now*/
.background-active.temp-margin-fix {
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  margin: -16px;
  padding: 12px 12px 0 12px;
}
</style>
